/**
 * Live activity labels for the per-subagent HUD lines.
 *
 * The parent thread's child registry only says a subagent EXISTS and is
 * `running` — the work itself is a black box. But every subagent runs in its
 * own thread, and that thread's `/stream` WebSocket carries the same live
 * signals the main thread's does: `tool_call_started` events (with the tool's
 * one-line `progress` text, e.g. "reading the Hermes MCP README"), completion
 * events, and streamed reasoning/answer chunks. This module holds ONE
 * MessageStream per active subagent and folds those signals into a short
 * "what it's doing right now" phrase for the subagent's line:
 *
 *   ⣯ Research Assistant — reading the Cloudflare email docs
 *
 * Lifecycle: `sync(ids)` is called after every subagent reconcile with the
 * authoritative set of running child thread ids — newcomers get a stream,
 * departed ids are closed and forgotten, so sockets never outlive their
 * subagent line. `closeAll()` tears everything down at session end.
 *
 * Fault tolerance is inherited from MessageStream: heartbeat + half-open
 * detection + exponential-backoff reconnect. A dropped socket therefore
 * degrades to a stale (or absent) phrase — never a crash, and never a stuck
 * line, because the line itself lives or dies by the registry reconcile, not
 * by this stream.
 */
import type { ApiClient } from "./api.ts";
import { MessageStream, type StreamHooks } from "./stream.ts";

/** What we need from a stream — MessageStream in production; tests inject a
 *  fake to drive the hooks directly. */
export interface ActivityStream {
  connect(): Promise<void>;
  close(): void;
}
export type ActivityStreamFactory = (threadId: string, hooks: StreamHooks) => ActivityStream;

/** Longest phrase we keep — the TUI clamps to the terminal width anyway; this
 *  just bounds what we store and hand over. */
const MAX_PHRASE = 120;

interface Entry {
  stream: ActivityStream;
  /** tool call id → progress phrase, insertion-ordered (last = most recent). */
  steps: Map<string, string>;
  /** Last phrase shown. Sticky: between tools we keep the previous phrase
   *  until a chunk/tool replaces it, so the line never flickers empty. */
  phrase: string | null;
}

/** One-line, bounded, human phrase from an event's progress text. The TUI
 *  sanitizes again at render time; this keeps stored state sane. */
function cleanPhrase(raw: string): string {
  const s = raw.replace(/\s+/g, " ").trim();
  return s.length > MAX_PHRASE ? `${s.slice(0, MAX_PHRASE - 1)}…` : s;
}

/** "web_search" → "web search" — a readable fallback when a tool has no
 *  progress text. */
function prettyToolName(name: string): string {
  return name.replace(/^provider:/, "").replace(/[_:-]+/g, " ").trim();
}

export class SubagentActivity {
  private entries = new Map<string, Entry>();

  constructor(
    api: ApiClient,
    /** Fired when a subagent's phrase changes; the consumer re-pushes the
     *  subagent lines to the TUI. */
    private onChange: (threadId: string) => void,
    /** Test seam — production always uses a real MessageStream. */
    private makeStream: ActivityStreamFactory = (threadId, hooks) => new MessageStream(api, threadId, hooks)
  ) {}

  /** The current activity phrase for a subagent's child thread, if any. */
  phraseFor(threadId: string): string | null {
    return this.entries.get(threadId)?.phrase ?? null;
  }

  /**
   * Reconcile the open streams against the authoritative set of RUNNING
   * subagent child-thread ids (from the parent's registry): open newcomers,
   * close and forget departed ones.
   */
  sync(activeIds: Iterable<string>): void {
    const want = new Set(activeIds);
    for (const [id, entry] of [...this.entries]) {
      if (!want.has(id)) {
        entry.stream.close();
        this.entries.delete(id);
      }
    }
    for (const id of want) {
      if (!this.entries.has(id)) this.open(id);
    }
  }

  private open(threadId: string): void {
    const entry: Entry = { stream: null as unknown as ActivityStream, steps: new Map(), phrase: null };

    const setPhrase = (phrase: string | null) => {
      if (phrase === entry.phrase) return;
      entry.phrase = phrase;
      this.onChange(threadId);
    };
    /** The most recently started still-active tool wins; with no active tool
     *  the previous phrase stays (sticky) rather than blinking empty. */
    const phraseFromSteps = (): string | null => {
      let last: string | null = null;
      for (const v of entry.steps.values()) last = v;
      return last ?? entry.phrase;
    };

    entry.stream = this.makeStream(threadId, {
      // Streamed output with no tool in flight means the model is composing —
      // reasoning reads as "thinking", visible answer text as "writing". These
      // only fire on TRANSITIONS (phrase comparison), not per chunk.
      onChunk: () => {
        if (entry.steps.size === 0) setPhrase("writing");
      },
      onReasoningChunk: () => {
        if (entry.steps.size === 0) setPhrase("thinking");
      },
      onAssistantText: () => {},
      onEvent: (eventType, data) => {
        if (eventType === "tool_call_started" && data?.id) {
          entry.steps.set(String(data.id), cleanPhrase(String(data.progress || prettyToolName(String(data.name || "")) || "working")));
          setPhrase(phraseFromSteps());
        } else if (eventType === "tool_call_done" && data?.id) {
          entry.steps.delete(String(data.id));
          setPhrase(phraseFromSteps());
        }
      },
      onError: () => {},
    });
    this.entries.set(threadId, entry);
    // Fire-and-forget: the stream resolves its own connection lifecycle
    // (reconnects with backoff); a subagent that finishes first is cleaned up
    // by the next sync().
    void entry.stream.connect();
  }

  /** Close every stream (session teardown). */
  closeAll(): void {
    for (const entry of this.entries.values()) entry.stream.close();
    this.entries.clear();
  }
}
